import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Award, Check, Pencil, Plus, Trash2 } from 'lucide-react';
import type { AwardToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '~/components/system/form-field';
import { serverMessage } from '~/lib/api/server-error';
import { formatDate } from '~/lib/format-date';
import { useContributorAwards, useContributorAwardMutations } from '~/hooks/use-contributor-subviews';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contributorId: number | null;
	contributorName?: string;
}

const EMPTY: AwardToken = { nm: '', descr: '', loc: '', award_date: null, iv: true, si: 0 };

/** Per-contributor awards sub-grid (legacy ContributorAwards popup). List + inline add/edit. */
export function ContributorAwardsDialog({ open, onOpenChange, contributorId, contributorName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading } = useContributorAwards(open ? contributorId : null);
	const { create, update, remove } = useContributorAwardMutations(contributorId ?? 0);
	const [editing, setEditing] = useState<AwardToken | null>(null);

	useEffect(() => {
		if (!open) setEditing(null);
	}, [open]);

	const set = (patch: Partial<AwardToken>) => setEditing(prev => ({ ...(prev ?? EMPTY), ...patch }));

	const save = async () => {
		if (!editing) return;
		if (!editing.nm?.trim()) {
			toast.error(t('validation.required'));
			return;
		}
		try {
			if (editing.id) await update.mutateAsync(editing);
			else await create.mutateAsync(editing);
			toast.success(t('common.save'));
			setEditing(null);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	const del = (a: AwardToken) => {
		if (!a.id) return;
		remove.mutate(a.id, { onSuccess: () => toast.success(t('common.delete')), onError: e => toast.error(serverMessage(e)) });
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('contributors.list.subviews.awards.title')}</DialogTitle>
					<DialogDescription>{contributorName}</DialogDescription>
				</DialogHeader>

				{editing ? (
					<div className="flex flex-col gap-3">
						<div className="grid grid-cols-2 gap-3">
							<FormField label={t('contributors.list.subviews.awards.name')}>
								<Input value={editing.nm ?? ''} onChange={e => set({ nm: e.target.value })} maxLength={200} autoFocus />
							</FormField>
							<FormField label={t('contributors.list.subviews.awards.location')}>
								<Input value={editing.loc ?? ''} onChange={e => set({ loc: e.target.value })} maxLength={200} />
							</FormField>
							<FormField label={t('contributors.list.subviews.awards.date')}>
								<Input
									type="date"
									value={(editing.award_date as string | null)?.slice(0, 10) ?? ''}
									onChange={e => set({ award_date: e.target.value || null })}
								/>
							</FormField>
							<FormField label={t('contributors.list.subviews.awards.sort')}>
								<Input
									type="number"
									value={editing.si ?? 0}
									onChange={e => set({ si: e.target.value === '' ? 0 : Number(e.target.value) })}
								/>
							</FormField>
						</div>
						<FormField label={t('contributors.list.subviews.awards.description')}>
							<Textarea value={editing.descr ?? ''} onChange={e => set({ descr: e.target.value })} rows={2} />
						</FormField>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="aw-iv">{t('contributors.list.subviews.awards.visible')}</Label>
							<Switch id="aw-iv" checked={Boolean(editing.iv)} onCheckedChange={next => set({ iv: next })} />
						</div>
						<div className="flex justify-between">
							<Button variant="ghost" onClick={() => setEditing(null)}>
								<ArrowLeft size={15} /> {t('common.cancel')}
							</Button>
							<Button onClick={save} disabled={create.isPending || update.isPending}>
								{t('common.save')}
							</Button>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<div className="flex justify-end">
							<Button size="sm" onClick={() => setEditing({ ...EMPTY })}>
								<Plus size={15} /> {t('contributors.list.subviews.awards.add')}
							</Button>
						</div>
						<div className="max-h-[55vh] overflow-auto rounded-md border border-border">
							{isLoading ? (
								<div className="flex flex-col gap-2 p-3">
									{Array.from({ length: 4 }).map((_, i) => (
										<Skeleton key={i} className="h-9 w-full rounded-md" />
									))}
								</div>
							) : !data || data.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-12 text-center">
									<Award size={32} className="text-foreground-disabled" strokeWidth={1.25} />
									<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
								</div>
							) : (
								<table className="w-full border-collapse text-sm">
									<thead className="bg-elevated text-xs text-foreground-muted">
										<tr className="border-b border-border">
											<th className="px-3 py-2 text-left font-medium">{t('contributors.list.subviews.awards.name')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('contributors.list.subviews.awards.location')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('contributors.list.subviews.awards.date')}</th>
											<th className="px-3 py-2 text-center font-medium">{t('contributors.list.subviews.awards.visible')}</th>
											<th className="px-2 py-2" style={{ width: 80 }} />
										</tr>
									</thead>
									<tbody>
										{data.map(a => (
											<tr key={a.id} className="border-b border-border/60 hover:bg-muted/50">
												<td className="px-3 py-2 text-foreground">{a.nm}</td>
												<td className="px-3 py-2 text-foreground-muted">{a.loc || '—'}</td>
												<td className="px-3 py-2 text-foreground-muted tabular-nums">
													{(a.award_date && formatDate(a.award_date as string)) || '—'}
												</td>
												<td className="px-3 py-2 text-center">
													{a.iv ? (
														<Check size={16} className="mx-auto text-success-fg" />
													) : (
														<span className="text-foreground-disabled">—</span>
													)}
												</td>
												<td className="px-2 py-1 text-center whitespace-nowrap">
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														aria-label={t('common.edit')}
														onClick={() => setEditing(a)}>
														<Pencil size={14} />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7 text-danger-fg"
														aria-label={t('common.delete')}
														onClick={() => del(a)}>
														<Trash2 size={14} />
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
